// utils/relative-time-it.ts
//
// W3 Phase A — Italian "tu" relative-time formatter for the Cronologia
// drawer items. Hand-rolled per CLAUDE.md "no new external date deps" rule.
//
// Buckets (chosen to match the spec's "tu" voice + match Claude.ai-style chat
// drawers, where the goal is "is this fresh / today / a few days old / older"
// rather than a precise relative count):
//
//   <60s        → "adesso"
//   <60min      → "{N} {minuto|minuti} fa"
//   <24h        → "{N} {ora|ore} fa"
//   <48h        → "ieri"
//   <7d         → "{N} giorni fa"
//   <14d        → "una settimana fa"
//   <30d        → "{N} settimane fa"
//   <60d        → "un mese fa"
//   <365d       → "{N} mesi fa"
//   ≥365d       → "{N} {anno|anni} fa"
//
// Future timestamps (clock skew) collapse to "adesso" — never produce
// "in 5 minuti" copy. Invalid inputs return "" so callers can hide the row.
//
// Italian "tu" voice: lowercase strings throughout (no leading capital);
// callers can apply text transforms if they need a leading cap.

/**
 * Format an ISO timestamp as a relative Italian "tu" string.
 *
 * @param iso  ISO 8601 timestamp (e.g. "2026-04-30T14:32:11Z").
 * @param nowMs  Optional override for the current time in ms. Used by tests.
 * @returns  Lowercase Italian relative-time string, or "" on parse failure.
 */
export function relativeTimeIt(iso: string, nowMs: number = Date.now()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diffMs = nowMs - t;

  // Clock skew → "adesso"
  if (diffMs < 0) return 'adesso';

  const sec  = Math.floor(diffMs / 1000);
  if (sec < 60) return 'adesso';

  const min  = Math.floor(sec / 60);
  if (min < 60) return min === 1 ? '1 minuto fa' : `${min} minuti fa`;

  const hr   = Math.floor(min / 60);
  if (hr < 24) return hr === 1 ? '1 ora fa' : `${hr} ore fa`;

  const day  = Math.floor(hr / 24);
  if (day < 2)   return 'ieri';
  if (day < 7)   return `${day} giorni fa`;
  if (day < 14)  return 'una settimana fa';
  if (day < 30)  return `${Math.floor(day / 7)} settimane fa`;
  if (day < 60)  return 'un mese fa';
  if (day < 365) return `${Math.floor(day / 30)} mesi fa`;

  const year = Math.floor(day / 365);
  return year === 1 ? '1 anno fa' : `${year} anni fa`;
}
